import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ISideMenuItem } from "./SideMenu";


/**
 * Picks the first menu item that has a navigable link.
 * Used by MainView components to find a sensible default page based on
 * the user's role-filtered side menu.
 */
export function getFirstAccessibleLink(items: ISideMenuItem[] | undefined, fallback?: string): string | undefined {
    return items?.find(i => !!i.link && !i.seperator)?.link ?? fallback
}

const lastPageByModule = new Map<string, string>();

/**
 * Used inside a module layout (e.g. CRMMainView, AccountsMainView).
 * - When the URL is exactly the module root, redirects to the last visited
 *   child page (in-memory, cleared on refresh) or the provided default.
 * - Whenever the URL is inside the module, remembers it for the rest of the session.
 */
export function useModuleLastPage(moduleRoot: string, defaultPath: string) {
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const root = moduleRoot.replace(/\/$/, "");
        const path = location.pathname;

        if (path === root || path === root + "/") {
            const stored = lastPageByModule.get(root);
            navigate(stored || defaultPath, { replace: true });
            return;
        }

        lastPageByModule.set(root, path + location.search);
    }, [location.pathname, location.search, moduleRoot, defaultPath, navigate]);
}
